import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Folder, FolderOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ImageUploadField } from "./ImageUploadField";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminCategories = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    image_url: ''
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    category_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, subcategoriesRes] = await Promise.all([
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/subcategories`)
      ]);
      setCategories(categoriesRes.data);
      setSubcategories(subcategoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Category created');
      }
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || category.image || ''
    });
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This will also delete all subcategories and may affect products.')) return;
    
    try {
      await axios.delete(`${API_URL}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '', image_url: '' });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  // Subcategory handlers
  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await axios.put(`${API_URL}/subcategories/${editingSubcategory.id}`, subcategoryForm, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Subcategory updated');
      } else {
        await axios.post(`${API_URL}/subcategories`, subcategoryForm, {
          headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
        });
        toast.success('Subcategory created');
      }
      resetSubcategoryForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save subcategory');
    }
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      name: subcategory.name,
      category_id: subcategory.category_id,
      description: subcategory.description || ''
    });
    setShowSubcategoryForm(true);
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (!window.confirm('Are you sure? This may affect products.')) return;
    
    try {
      await axios.delete(`${API_URL}/subcategories/${subcategoryId}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` }
      });
      toast.success('Subcategory deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subcategory');
    }
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({ name: '', category_id: '', description: '' });
    setEditingSubcategory(null);
    setShowSubcategoryForm(false);
  };

  const getCategorySubcategories = (categoryId) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl font-bold">Category Management</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowCategoryForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
          <Button variant="outline" onClick={() => setShowSubcategoryForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Subcategory
          </Button>
        </div>
      </div>

      {/* Category Form */}
      {showCategoryForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <Label htmlFor="cat-name">Category Name *</Label>
                <Input
                  id="cat-name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="cat-desc">Description</Label>
                <Textarea
                  id="cat-desc"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <ImageUploadField
                  label="Category Image"
                  value={categoryForm.image_url}
                  onChange={(val) => setCategoryForm({ ...categoryForm, image_url: val })}
                  onSave={() => {}}
                  token={token}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingCategory ? 'Update' : 'Create'} Category
                </Button>
                <Button type="button" variant="outline" onClick={resetCategoryForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Subcategory Form */}
      {showSubcategoryForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubcategorySubmit} className="space-y-4">
              <div>
                <Label htmlFor="sub-category">Parent Category *</Label>
                <select
                  id="sub-category"
                  value={subcategoryForm.category_id}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, category_id: e.target.value })}
                  className="flex h-11 w-full rounded-lg border border-input bg-background px-4 py-2 text-sm"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="sub-name">Subcategory Name *</Label>
                <Input
                  id="sub-name"
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="sub-desc">Description</Label>
                <Textarea
                  id="sub-desc"
                  value={subcategoryForm.description}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  {editingSubcategory ? 'Update' : 'Create'} Subcategory
                </Button>
                <Button type="button" variant="outline" onClick={resetSubcategoryForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Categories List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => {
          const categorySubs = getCategorySubcategories(category.id);
          const isExpanded = selectedCategory === category.id;
          
          return (
            <Card key={category.id} className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  {(category.image_url || category.image) ? (
                    <img
                      src={category.image_url || category.image}
                      alt={category.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                      <Folder className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-1">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {category.description}
                      </p>
                    )}
                  </div>
                </div>

                {categorySubs.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setSelectedCategory(isExpanded ? null : category.id)}
                      className="flex items-center gap-2 text-sm text-primary hover:underline mb-2"
                    >
                      {isExpanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                      {categorySubs.length} Subcategories
                    </button>
                    {isExpanded && (
                      <div className="space-y-2 pl-4 border-l-2 border-muted">
                        {categorySubs.map(sub => (
                          <div key={sub.id} className="flex items-center justify-between group">
                            <span className="text-sm">{sub.name}</span>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-smooth">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleEditSubcategory(sub)}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleDeleteSubcategory(sub.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEditCategory(category)}
                  >
                    <Edit2 className="w-3 h-3 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {categories.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Folder className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No categories yet</p>
            <Button onClick={() => setShowCategoryForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Category
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};